import { DataListFields } from '@models/module/main';

export const PROGRESS_PAYMENT_NESTED_TABLE_PAGE_SIZE = 20;

export type ProgressPaymentRequestParams<B = unknown> = {
  contractId: number;
  entityInstanceId: number;
  paymentPeriodId: number;
  moduleTypeId: number;
  workflowId: number;
  screenId: number;
  moduleId: number;
  body: B;
};

/** The root table & nested tables */
export type ProgressPaymentItemTable = {
  fields?: ProgressPaymentItemTableField[];
  isBlocked: boolean;
  items: ProgressPaymentItemRow[];
  moduleAbbr?: string;
  moduleId: number;
  moduleName?: string;
  payByQuantityEnabled: boolean;
  totals?: ProgressPaymentItemTotals;
};

export type ProgressPaymentItemRow = {
  parent_entity_instance_id: number;
  child_entity_instance_id: number;
  module_id: number;
  relation_id: number;
  relation_instance_id: number;
  IsParentSubItem: boolean;
  is_blocked: boolean;
  paid_over_100: boolean;
  is_modified: boolean;
  can_pay_over_100: boolean;
  total_payment: number;
  payment_amount: number;
  payment_percent: number;
  payment_qty: number;
  previous_payment_amount: number;
  previous_payment_percent: number;
  previous_payment_qty: number;
  paid_amount: number;
  paid_percent: number;
  paid_qty: number;
  balance_to_finish: number;
  full_doc_num: string;
  child_entity_id: number;
  item_count: number;
  sort_column: string;
  ids: string;
  level: number;
  data_list_id: number;
  updated_by: string;
  updated_on: string;
  initiator: string;
  created_by_role_id: number;
  initiator_role: string;
  created_on: string;
  doc_num_increment: number;
  is_draft: boolean;
  state_color: string;
  state_text_color: string;
  state_name: string;
  state_type: string;
  is_current: boolean;
  revision: number;
  date_submitted: string;
  closed_on: string;
  daysopened: number;
  screen_name: string;
  screen_label: string;
  module_name?: string;
  module_abbr?: string;
  description?: string;
  sov_wbs_id?: number;
  qty_total?: number;
  /** A row can contain inner tables. This is the same type as the root table. */
  items?: ProgressPaymentItemTable[];
  [key: string]: unknown;
};

export type ProgressPaymentItemTableField = DataListFields & {
  canBeModifiedOnPP: boolean;
};

export type ProgressPaymentItemTotals = {
  total_payment: number;
  payment_amount: number;
  payment_percent: number;
  previous_payment_amount: number;
  previous_payment_percent: number;
  paid_amount: number;
  paid_percent: number;
  balance_to_finish: number;
  [key: string]: unknown;
};

export type ProgressPaymentModulesResponse = {
  modules: ProgressPaymentModule[];
  ppTotals: {
    totalAmount: number;
    paymentAmount: number;
    retainedAmount: number;
    releasedAmount: number;
    finalAmount: number;
  };
  totalsDecimals: {
    paymentAmount: number;
    paymentPercent: number;
    paymentTotal: number;
    paymentQty: number;
  };
  canPayReleases: boolean;
  releases: {
    itemsCount: number;
    total_payment: number;
    payment_amount: number;
    payment_percent: number;
  };
};

export type ProgressPaymentModule = {
  relationId: number;
  entityId: number;
  moduleId: number;
  isWBS: boolean;
  sovEntityInstanceId: number;
  moduleName: string;
  fields: ProgressPaymentItemTableField[];
  itemsCount: number;
  total_payment: number;
  payment_amount: number;
  payment_percent: number;
};

/** Per-page segment for progress payment items (offset pagination). */
export type ProgressPaymentItemsPageDescriptor = {
  /** Cursor passed to `GetProgressPaymentItems` (`SearchViewModel.Page`); not a linear parent-row index. */
  offset: number;
  /** 1-based page number; first page is 1. */
  pageNo: number;
  pageSize: number;
  /** Parent (root) rows on this page; use cumulative sums for "Items X–Y of Z" in the UI. */
  parentItems: number;
};

export type ProgressPaymentItemsPagesResponse = ProgressPaymentItemsPageDescriptor[];

/** Server-driven pagination labels for the progress-payment nested table (root only). */
export type ProgressPaymentNestedTablePaginationView = {
  pageIndex: number;
  pageNo: number;
  totalPages: number;
  itemStart: number;
  itemEnd: number;
  totalItems: number;
};

export enum PaymentTypeEnum {
  Percent = 1,
  Amount = 2,
  Quantity = 3,
}

export const PaymentTypeFields: Record<PaymentTypeEnum, string> = {
  [PaymentTypeEnum.Percent]: 'payment_percent',
  [PaymentTypeEnum.Amount]: 'payment_amount',
  [PaymentTypeEnum.Quantity]: 'payment_qty',
} as const;
